/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-15
 */

// Add third-party dependencies.
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Paper,
  Button,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

// Add local dependencies.
import Header from '../../components/layout/Header';
import StoreSelector from './components/StoreSelector';
import StoreDialog from './components/StoreDialog';
import ItemManagementTable from './components/ItemManagementTable';
import {
  useGetItemsQuery,
  useBulkUpdateItemsMutation,
} from '../../api/shoppingListApi';

/**
 * Shopping List page component.
 * Main landing page for the Shopping List tool.
 *
 * @returns {React.ReactElement} The shopping list page component
 *
 * @example
 * return (
 *   <ShoppingListPage />
 * );
 */
const ShoppingListPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management - initialize from localStorage
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(
    () => localStorage.getItem('selectedStoreId') || null
  );
  const [isStoreDialogOpen, setIsStoreDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<number>(0);

  const { data: items = [] } = useGetItemsQuery(selectedStoreId || '', {
    skip: !selectedStoreId,
  });
  const [bulkUpdateItems] = useBulkUpdateItemsMutation();

  const handleStoreChange = (storeId: string | null) => {
    setSelectedStoreId(storeId);
    // Save to localStorage
    if (storeId) {
      localStorage.setItem('selectedStoreId', storeId);
    } else {
      localStorage.removeItem('selectedStoreId');
    }
  };

  const handleAddStore = () => {
    setIsStoreDialogOpen(true);
  };

  const handleCloseStoreDialog = () => {
    setIsStoreDialogOpen(false);
  };

  const handleStoreCreated = () => {
    // Dialog will close automatically
    // Store list will refresh via RTK Query
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleDoneShopping = async () => {
    // Find all items that are checked off and uncheck them on the master list
    const checkedOffItems = items
      .filter(item => item.is_on_list && item.is_checked_off)
      .map(item => ({ id: item.id, is_on_list: false, is_checked_off: false }));

    if (checkedOffItems.length > 0) {
      try {
        await bulkUpdateItems({
          data: { item_updates: checkedOffItems },
          storeId: selectedStoreId!,
        }).unwrap();
      } catch (err) {
        console.error('Failed to update items:', err);
      }
    }

    // Switch back to master list tab
    setCurrentTab(0);
  };

  // Count checked off items
  const checkedOffCount = items.filter(
    item => item.is_on_list && item.is_checked_off
  ).length;

  // Count items on shopping list
  const itemsOnListCount = items.filter(item => item.is_on_list).length;

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 1, sm: 2, md: 3 },
          pb: isMobile ? 10 : 3,
        }}
      >
        <Box sx={{ mb: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            }}
          >
            Shopping List
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
              lineHeight: 1.4,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Manage your shopping lists for different stores with organized item
            tracking.
          </Typography>
        </Box>

        {/* Store Selector */}
        <StoreSelector
          selectedStoreId={selectedStoreId}
          onStoreChange={handleStoreChange}
          onAddStore={handleAddStore}
        />

        {/* Tabs and Content (only show when store is selected) */}
        {selectedStoreId && (
          <Paper sx={{ mt: 2 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: { xs: 1, sm: 2 },
              }}
            >
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label="shopping list tabs"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    minHeight: { xs: 44, sm: 48 },
                  },
                }}
              >
                <Tab label="Master List" />
                <Tab
                  label={`Shopping List${itemsOnListCount > 0 ? ` (${itemsOnListCount})` : ''}`}
                />
              </Tabs>

              {/* Done Shopping button - only show on shopping list tab */}
              {currentTab === 1 && (
                <Button
                  onClick={handleDoneShopping}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  disabled={checkedOffCount === 0}
                  size="small"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    ml: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    Done Shopping
                    {checkedOffCount > 0 && ` (${checkedOffCount})`}
                  </Box>
                  <Box
                    component="span"
                    sx={{ display: { xs: 'inline', sm: 'none' } }}
                  >
                    Done
                  </Box>
                </Button>
              )}
            </Box>

            {/* Tab Panels */}
            <Box role="tabpanel" hidden={currentTab !== 0}>
              {currentTab === 0 && (
                <ItemManagementTable
                  storeId={selectedStoreId}
                  isShoppingListView={false}
                />
              )}
            </Box>

            <Box role="tabpanel" hidden={currentTab !== 1}>
              {currentTab === 1 && (
                <ItemManagementTable
                  storeId={selectedStoreId}
                  isShoppingListView
                />
              )}
            </Box>
          </Paper>
        )}

        {/* Store Dialog */}
        <StoreDialog
          open={isStoreDialogOpen}
          onClose={handleCloseStoreDialog}
          onSuccess={handleStoreCreated}
        />
      </Container>
    </Box>
  );
};

export default ShoppingListPage;
